import { useAuthStore } from "@/store/use-auth-store";
import { isIOS, isPWA } from "@/utils/util";
import { UserManager, WebStorageStateStore } from "oidc-client-ts";
import { AuthProvider, useAuth } from "oidc-react";
import { useEffect, useState } from "react";

type Props = {
  children: React.ReactNode;
};

function oidcUserKey(authority: string, clientId: string) {
  return `oidc.user:${authority}:${clientId}`;
}

/**
 * oidc-client-ts defaults to sessionStorage, which is tab-scoped. New tabs
 * therefore have no refresh token and fall back to a cross-origin silent
 * iframe that modern browsers block. Persist the user in localStorage so
 * tabs share the session; keep PKCE state in sessionStorage.
 */
function migrateOidcUserToLocalStorage(authority: string, clientId: string) {
  const key = oidcUserKey(authority, clientId);
  if (localStorage.getItem(key)) return;
  const fromSession = sessionStorage.getItem(key);
  if (fromSession) {
    localStorage.setItem(key, fromSession);
    sessionStorage.removeItem(key);
  }
}

/**
 * After IdP logout the browser may restore this page from bfcache with stale
 * in-memory OIDC state while storage was already cleared. Reload to sync.
 * Use UserManager.getUser() — scanning all oidc.user:* keys false-positives on
 * stale authorities and skips the reload.
 */
function BfCacheAuthSync() {
  const { userData, userManager } = useAuth();

  useEffect(() => {
    const onPageShow = (event: PageTransitionEvent) => {
      if (!event.persisted) return;
      void userManager.getUser().then((stored) => {
        const hasStoredSession = !!stored && !stored.expired;
        if (!hasStoredSession && userData) {
          window.location.reload();
        }
      });
    };
    window.addEventListener("pageshow", onPageShow);
    return () => window.removeEventListener("pageshow", onPageShow);
  }, [userData, userManager]);

  return null;
}

export default function OidcClientProvider({ children }: Props) {
  const [userManager, setUserManager] = useState<UserManager | null>(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    async function fetchConfig() {
      try {
        const response = await fetch(`/membersapp/config.json?t=${Date.now()}`, {
          cache: "no-store",
          headers: {
            "Cache-Control": "no-cache, no-store, must-revalidate",
            Pragma: "no-cache",
            Expires: "0"
          }
        });
        const data = await response.json();
        const authority = data?.authApiUri as string;
        const clientId = data?.OidcClientId as string;
        migrateOidcUserToLocalStorage(authority, clientId);

        const manager = new UserManager({
          client_id: clientId,
          redirect_uri: `${data?.basePath}/callback`,
          silent_redirect_uri: `${data?.basePath}/silent_renew`,
          post_logout_redirect_uri: data?.basePath,
          automaticSilentRenew: true,
          scope: "openid profile email entryevent offline_access",
          authority,
          response_type: "code",
          userStore: new WebStorageStateStore({ store: window.localStorage }),
          extraHeaders: {
            "X-Pwa-App": String(isPWA()) ?? false,
            "X-Client-Platform-Ios": String(isIOS()) ?? false
          }
        });

        setUserManager(manager);
      } catch (error) {
        console.log("Error fetching config: ", error);
      }
      setLoading(false);
    }

    void fetchConfig();
  }, []);

  if (loading || !userManager) return null;

  return (
    <AuthProvider
      userManager={userManager}
      autoSignIn={false}
      onSignOut={() => {
        useAuthStore.getState().setWhoami(null);
        useAuthStore.getState().setProfileImage("");
      }}
    >
      <BfCacheAuthSync />
      {children}
    </AuthProvider>
  );
}
